import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'

interface LikeButtonProps {
  projectId: string
}

export default function LikeButton({ projectId }: LikeButtonProps) {
  const [isLiked, setIsLiked] = useState(() => {
    // Persist likes in localStorage
    const likes = JSON.parse(localStorage.getItem('likedProjects') || '[]')
    return likes.includes(projectId)
  })
  const [isAnimating, setIsAnimating] = useState(false)

  const handleLike = () => {
    const newLiked = !isLiked
    setIsLiked(newLiked)
    setIsAnimating(true)

    // Persist to localStorage
    const likes = JSON.parse(localStorage.getItem('likedProjects') || '[]')
    if (newLiked) {
      localStorage.setItem('likedProjects', JSON.stringify([...likes, projectId]))
    } else {
      localStorage.setItem('likedProjects', JSON.stringify(likes.filter((id: string) => id !== projectId)))
    }

    // Reset animation state
    setTimeout(() => setIsAnimating(false), 600)
  }

  return (
    <motion.button
      onClick={handleLike}
      className="relative w-10 h-10 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-smooth overflow-visible"
      whileTap={{ scale: 0.9 }}
    >
      {/* Burst particles */}
      <AnimatePresence>
        {isAnimating && isLiked && (
          <>
            {[...Array(6)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-red-400"
                initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                animate={{
                  scale: [0, 1, 0],
                  x: Math.cos((i * 60 * Math.PI) / 180) * 24,
                  y: Math.sin((i * 60 * Math.PI) / 180) * 24,
                  opacity: [1, 1, 0],
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Ring burst */}
      <AnimatePresence>
        {isAnimating && isLiked && (
          <motion.span
            className="absolute inset-0 rounded-full border-2 border-red-400"
            initial={{ scale: 0.8, opacity: 1 }}
            animate={{ scale: 1.8, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>

      {/* Heart icon */}
      <motion.svg
        className="w-5 h-5 md:w-6 md:h-6"
        fill={isLiked ? 'currentColor' : 'none'}
        stroke={isLiked ? 'none' : 'currentColor'}
        strokeWidth={2}
        viewBox="0 0 24 24"
        animate={isAnimating && isLiked ? {
          scale: [1, 1.3, 0.9, 1.1, 1],
        } : {}}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{ color: isLiked ? '#f87171' : 'white' }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </motion.svg>
    </motion.button>
  )
}
